import { weatherCodeParts, type CloudAmount, type CloudLayer, type FlightCategory } from '@jupi/shared'

const pad3 = (n: number) => String(Math.round(n) % 360).padStart(3, '0')

export function windText(
  directionDeg: number | null,
  speedKt: number | null,
  gustKt: number | null,
  calmLabel: string,
) {
  if (speedKt === null) return null
  if (speedKt === 0) return calmLabel
  const direction = directionDeg === null ? 'VRB' : `${pad3(directionDeg)}°`
  return `${direction} / ${speedKt} kt${gustKt !== null && gustKt > speedKt ? ` G${gustKt}` : ''}`
}

export function visibilityText(metres: number | null) {
  if (metres === null) return null
  if (metres >= 9999) return '10 km+'
  if (metres >= 5000) return `${Math.floor(metres / 1000)} km`
  return `${metres} m`
}

export function cloudsText(clouds: CloudLayer[], cavok = false) {
  if (cavok) return 'CAVOK'
  if (clouds.length === 0) return null
  return clouds
    .map((c) => {
      if (c.baseFt === null) return c.amount
      return `${c.amount} ${c.baseFt} ft${c.type ? ` ${c.type}` : ''}`
    })
    .join(', ')
}

export const temperatureText = (t: number | null, dew: number | null) =>
  t === null ? null : dew === null ? `${Math.round(t)} °C` : `${Math.round(t)} / ${Math.round(dew)} °C`

const COMPASS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'] as const
export const compassPoint = (bearingDeg: number) => COMPASS[Math.round(bearingDeg / 45) % 8]!

// One change group per line; PROB40 TEMPO stays together.
export const tafLines = (raw: string) =>
  raw
    .replace(/\s+(?=(?:PROB\d{2}|BECMG|FM\d{6})\b)/g, '\n  ')
    .replace(/(?<!PROB\d{2})\s+(?=TEMPO\b)/g, '\n  ')

export const FLIGHT_CATEGORY_COLORS: Record<FlightCategory, string> = {
  VFR: '#2e7d32',
  MVFR: '#1565c0',
  IFR: '#c62828',
  LIFR: '#ad1457',
}

// "BKN 1200 ft (5–7/8)".
const OKTAS: Partial<Record<CloudAmount, string>> = { FEW: '1–2/8', SCT: '3–4/8', BKN: '5–7/8', OVC: '8/8' }

export function cloudsWithOktas(clouds: CloudLayer[]) {
  if (clouds.length === 0) return null
  return clouds
    .map((c) => {
      const base = c.baseFt === null ? c.amount : `${c.amount} ${c.baseFt} ft${c.type ? ` ${c.type}` : ''}`
      return OKTAS[c.amount] ? `${base} (${OKTAS[c.amount]})` : base
    })
    .join(', ')
}

type Translate = (key: string, options?: Record<string, unknown>) => string

// "-SHRA" -> "Light showers of rain". TS and a lone descriptor (VCSH) have
// their own phrasing.
export function describeWeather(code: string, t: Translate): string {
  const parts = weatherCodeParts(code)
  if (!parts) return code
  const what = parts.phenomena.map((p) => t(`weather.wx.phenomenon.${p}`)).join(t('weather.wx.and'))
  let text: string
  if (parts.descriptor === 'TS') {
    text = what ? t('weather.wx.thunderstormWith', { what }) : t('weather.wx.alone.TS')
  } else if (parts.descriptor && !what) {
    text = t(`weather.wx.alone.${parts.descriptor}`)
  } else {
    text = [parts.descriptor ? t(`weather.wx.descriptor.${parts.descriptor}`) : null, what].filter(Boolean).join(' ')
  }
  if (parts.intensity === 'light' || parts.intensity === 'heavy') text = `${t(`weather.wx.${parts.intensity}`)} ${text}`
  if (parts.intensity === 'vicinity') text = `${text} ${t('weather.wx.vicinity')}`
  return text.charAt(0).toUpperCase() + text.slice(1)
}

// "27.9. 09:00" (UTC): TAF times need the day.
export function utcDayAndTime(iso: string) {
  const d = new Date(iso)
  return `${d.getUTCDate()}.${d.getUTCMonth() + 1}. ${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`
}
